/**
 * Metrics Grid Block Renderer
 * Renders a structured grid of performance metrics with values, changes, and labels
 */

import type { UIBlock, UIActionEvent, MetricsGridBlock } from "@/types/ui-blocks";

export interface MetricsGridBlockRendererProps {
  block: UIBlock;
  onUIAction: (event: UIActionEvent) => void;
}

export function MetricsGridBlockRenderer({ block }: MetricsGridBlockRendererProps) {
  if (block.type !== "metrics_grid") return null;

  const { content } = block as MetricsGridBlock;

  const columns = content.columns ?? 3;
  const nonEmptyCount = content.cells.filter(
    (c) => c.value !== null && c.value !== undefined && c.value !== ""
  ).length;

  // Calculate the number of rows needed to display all non-empty cells
  const rows = Math.ceil(nonEmptyCount / columns);
  const totalCells = rows * columns;

  // Build a fixed-length list and read from content.cells without mutating types
  const cells = Array.from({ length: totalCells }, (_, i) => content.cells[i]);

  return (
    <div className="mx-auto mt-6">
      {/* Header */}
      {content.header && (
        <div className="bg-custom-bg-primary border border-gray-200 rounded-t-xl px-4 py-2">
          <span className="text-xs text-custom-text-secondary">{content.header}</span>
        </div>
      )}

      {/* Grid Container */}
      <div
        className={`grid gap-0 border-l border-r border-b border-gray-200 ${
          content.header ? "rounded-b-xl" : "rounded-xl"
        } overflow-hidden`}
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {cells.map((cell, i) => {
          const isLastColumn = (i % columns) === (columns - 1);
          const currentRow = Math.floor(i / columns);
          const isLastRow = currentRow === (rows - 1);

          return (
            <div
              key={i}
              className={`p-4 bg-white hover:bg-gray-50 transition-colors min-h-[79px] ${
                !isLastColumn ? "border-r " : ""
              }${!isLastRow ? "border-b " : ""}border-gray-200`}
            >
            {cell && cell.value !== null && cell.value !== undefined ? (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-xl font-medium text-custom-text-primary">
                    {cell.value}
                  </span>
                  {(() => {
                    // Determine which indicator to display (change takes precedence)
                    // Empty strings are treated as no value
                    const displayValue =
                      (cell.change != null && cell.change !== '')
                        ? cell.change
                        : (cell.progress != null && cell.progress !== '')
                          ? cell.progress
                          : null;

                    return displayValue !== null ? (
                      <span
                        className={`text-xs font-normal ${
                          cell.changeColor || "text-gray-600"
                        }`}
                      >
                        {displayValue}
                      </span>
                    ) : null;
                  })()}
                </div>
                {cell.label && (
                  <div className="text-xs text-custom-text-secondary font-normal">
                    {cell.label}
                  </div>
                )}
              </>
            ) : null}
          </div>
          );
        })}
      </div>
    </div>
  );
}